import { cx } from '@flowus/common/cx';
import { getBizTrackerPlatform } from '@flowus/common/get-tracker-platform';
import { getClipboardApi } from '@flowus/common/hooks/ad-utils';
import { getSessionOp, SessionKeys, setSessionStorage } from '@flowus/common/storage';
import { refreshSessionParams } from '@flowus/common/storage/activity';
import { tryGetPromotionChannel } from '@flowus/login/hook/promotion-channel';
import type { SpaceDTO } from '@next-space/fe-api-idl';
import { BlockType } from '@next-space/fe-api-idl';
import produce from 'immer';
import { assign } from 'lodash-es';
import type { FC } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { LoadingContainer } from 'src/common/components/loading-container';
import { request } from 'src/common/request';
import { isPublicPageDeleted } from 'src/common/utils/is-public-page-deleted';
import { parseCustomDomain } from 'src/common/utils/url-utils';
import { HELPER_CANTER_ID } from 'src/components/ai-chat';
import * as Empty from 'src/components/empty';
import { PayAccessFee } from 'src/components/empty/pay-access-fee';
import { FilePreview } from 'src/components/file-preview';
import { Footer } from 'src/components/footer';
import { ImagesProvider } from 'src/components/images-provider';
import { KnowledgeAIButton } from 'src/components/service-center';
import { useExpandToc } from 'src/hooks/block/use-navs';
import { useFetchPage, useFetchRootPages } from 'src/hooks/page';
import { checkPageId } from 'src/hooks/page/check-page-id';
import { useCopyListener } from 'src/hooks/page/use-copy-listener';
import { useMouseEvent } from 'src/hooks/page/use-mouse-event';
import { usePageMeta } from 'src/hooks/page/use-page-meta';
import { usePageShortcut } from 'src/hooks/page/use-page-shortcut';
import { useCopyToSpace } from 'src/hooks/share/use-copy-to-space';
import { usePermissions } from 'src/hooks/share/use-permissions';
import { fetchSpaces } from 'src/hooks/space';
import { useOpenWatermark } from 'src/hooks/space/use-open-watermark';
import { useCurrentUser, useFetchSharePageUser } from 'src/hooks/user';
import { getUserInfoByApi } from 'src/hooks/user/get-user';
import { UPDATE_BLOCK } from 'src/redux/actions/block';
import { dispatch, getState } from 'src/redux/store';
import { $appUiStateCache, setAppUiState, useFileIllegal } from 'src/services/app';
import { useGlobalModalQueueEffect } from 'src/services/global-modal-queue';
import { $setOtherSpaceState } from 'src/services/other-space/state';
import { updatePublicSpaces } from 'src/services/public-space';
import { $spacesCache, updateSpaces } from 'src/services/spaces/spaces';
import { $currentUserCache } from 'src/services/user/current-user';
import { $searchParams, SearchParams, ViewPath } from 'src/utils';
import { bizTracker } from 'src/utils/biz-tracker';
import { isLinkTableBlock } from 'src/utils/block-type-utils';
import { extractPageIdFromPathname, useGetPageId } from 'src/utils/getPageId';
import { splicePathSearchParams } from 'src/utils/history-utils';
import { usePickBlock } from 'src/utils/pick-block';
import { qs } from 'src/utils/querystring';
import { useGetRightPageId } from 'src/utils/right-utils';
import { ShareAside } from '../main/aside';
import { Drawer } from '../main/drawer/drawer';
import { useDrawerOpenState } from '../main/drawer/shared';
import { Header } from '../main/header';
import { Layout } from '../main/layout';
import { Page } from '../main/page';
import { RightPage, RightPageOut } from '../main/right-page';
import { PageScene, PageSceneContext } from '../main/scene-context';
import { SharePasswordPage } from './share-password';
import { UseTemplateButton } from './use-template-button';
import { getCurrentSpaceId } from 'src/hooks/space/get-space';

export const SharePage: FC = () => {
  useCopyListener(true);
  usePageShortcut(true);
  useMouseEvent(true);
  const rightPageId = useGetRightPageId();
  const pageId = useGetPageId();
  const history = useHistory();
  const fetchUser = useFetchSharePageUser();
  const user = useCurrentUser();
  const copyToSpace = useCopyToSpace();
  const { allowShowSidebar } = usePermissions(pageId);
  const pageMeta = usePageMeta(pageId);
  const rightDivRef = useRef<HTMLDivElement>(null);
  const block = usePickBlock(pageId, []);
  const [_, setDrawerState] = useDrawerOpenState();
  useOpenWatermark(block?.spaceId);
  const fetchRootPages = useFetchRootPages();

  useEffect(() => {
    if (user.uuid) {
      const spaceId = getCurrentSpaceId();
      void fetchRootPages(spaceId);
    }
  }, [fetchRootPages, user.uuid]);

  useEffect(() => {
    // :)
    void getClipboardApi();
    void fetchUser().then((res) => {
      const query = qs.parse(location.search);
      const isCopyTemplateFromWebsite = query.from === 'website' && query.event === 'copy';
      if (isCopyTemplateFromWebsite && !res?.uuid) {
        history.push(
          splicePathSearchParams(ViewPath.login, {
            key: SearchParams.callbackPath,
            value: location.href,
          })
        );
      }
    });
  }, [fetchUser, history]);

  useEffect(() => {
    const curPageId = extractPageIdFromPathname(location.pathname);
    if (curPageId === HELPER_CANTER_ID) {
      const query = new URLSearchParams(location.search);
      const openAI = query.get(SearchParams.openAI);
      openAI && setDrawerState('ai-chat');
    }
  }, [setDrawerState]);

  useEffect(() => {
    // 不要用外面的pageId，要不然每次都会拉空间信息，没必要
    const curPageId = extractPageIdFromPathname(location.pathname);

    if (user.uuid) {
      const params = new URLSearchParams(location.search);
      if (params.get('from') === 'website' && params.get('event') === 'copy') {
        void fetchSpaces().then(() => {
          curPageId && copyToSpace(curPageId);
        });
      }
    }
  }, [copyToSpace, user.uuid]);

  const shareAside = useMemo(() => {
    return <ShareAside />;
  }, []);

  if (pageMeta?.tag === 'NEED_PASSWORD' && pageId) {
    return <SharePasswordPage uuid={pageId} />;
  }

  return (
    <PageSceneContext.Provider value={PageScene.SHARE}>
      <Layout aside={allowShowSidebar && !$searchParams.embed ? shareAside : null}>
        <div
          className={
            !$searchParams.print
              ? 'relative flex flex-1 h-full min-h-0 w-full overflow-x-hidden'
              : ''
          }
        >
          <ShareContent />
          {rightPageId ? (
            <RightPage uuid={rightPageId} rightDivRef={rightDivRef}>
              <ShareContent />
            </RightPage>
          ) : (
            <RightPageOut />
          )}
        </div>
      </Layout>
    </PageSceneContext.Provider>
  );
};

export const ShareContent: FC = () => {
  const pageId = useGetPageId();
  const [pageLoading, setPageLoading] = useState(true);
  const fetchPage = useFetchPage();
  const history = useHistory();
  const block = usePickBlock(pageId, []);
  const fileIllegal = useFileIllegal();
  const pageMeta = usePageMeta(pageId);
  const user = useCurrentUser();
  const { shared, allowSelectionCopy } = usePermissions(pageId);
  const firstLoading = useRef(true);
  const expandToc = useExpandToc();

  useEffect(() => {
    if (user.uuid) {
      if (pageMeta?.tag === 'NO_PERMISSION' && !shared) {
        void checkPageId(pageId).then(() => {
          history.replace(`/${pageId}`);
        });
      }
    }
  }, [history, pageId, pageMeta, shared, user.uuid]);

  useEffect(() => {
    const root = document.getElementById('root');
    if (!allowSelectionCopy) {
      root?.classList.add('select-none');
    } else {
      root?.classList.remove('select-none');
    }
  }, [allowSelectionCopy]);

  useEffect(() => {
    if (!pageId) {
      setPageLoading(false);
      return;
    }

    const { blocks } = getState();

    if (blocks[pageId]?.local) {
      dispatch(
        UPDATE_BLOCK({
          uuid: pageId,
          ignoreOp: true,
          patch: { local: false }, // 更新 local 状态
        })
      );
      setPageLoading(false);
      return;
    }

    setPageLoading(true);

    const psw = localStorage.getItem(`share-psw-${pageId}`);

    if (psw) {
      setSessionStorage(SessionKeys.PASSWORD, psw);
    }

    void fetchPage(pageId).finally(async () => {
      const { blocks } = getState();
      const block = blocks[pageId];
      try {
        if (!block?.spaceId) return;
        let space = $spacesCache[block.spaceId];
        setAppUiState({ $currentSpaceId: block.spaceId });
        const res = await request.infra.getSpacesPublicData({
          spaceIds: [block.spaceId],
        });
        if (!space) {
          updateSpaces(res.spaces as Record<string, SpaceDTO>);
        }
        updatePublicSpaces(res.spaces);
        // 减少一次getSpacesPublicData请求
        $setOtherSpaceState(produce((pre) => assign(pre, { [block.spaceId]: space })));
        const customDomain = parseCustomDomain(location.pathname);
        space = $spacesCache[block.spaceId];
        if (!space) return;

        bizTracker.addCommonParams(
          {
            spaceID: space.uuid,
            space_version: space.planType,
            space_members: space.permissions?.filter((p) => p.type === 'user' && p.userId).length,
            user_occupation: $currentUserCache.uuid ? $currentUserCache.occupation : undefined,
            ...getBizTrackerPlatform(),
          },
          true
        );
        const { domain } = space;
        if (domain && domain !== customDomain && !$appUiStateCache.$publicHomePage) {
          const path = domain
            ? `/${domain}/share/${pageId}${location.search}${location.hash}`
            : `/share/${pageId}${location.search}${location.hash}`;
          history.replace(path);
        }
        const isLinkTable = isLinkTableBlock(block);
        if (isLinkTable) {
          const reallyBlockId = block.data.ref?.uuid;
          if (reallyBlockId) {
            await fetchPage(reallyBlockId);
          }
        }
      } finally {
        // 一定要执行这个，否则没打开分享的share链接会一直loading
        setPageLoading(false);
        if (firstLoading.current) {
          firstLoading.current = false;
          expandToc(pageId, { includeItself: true });
        }
      }
      setAppUiState({ $pageRootLoaded: true });
    });
  }, [expandToc, fetchPage, history, pageId]);

  if (pageLoading) {
    return <LoadingContainer />;
  }

  if (fileIllegal) {
    return (
      <div className="flex flex-col w-full">
        <Empty.NoPermission uuid={block?.uuid ?? pageId} className="h-full" />
        <Footer logo={false} />
      </div>
    );
  }

  if (pageMeta?.tag === 'NEED_PAY') {
    return <PayAccessFee />;
  }

  if (block?.type === BlockType.FILE) {
    return <FilePreview uuid={block.uuid} isPreviewPage />;
  }

  if (isPublicPageDeleted(pageId) || pageMeta?.tag === 'NOT_FOUND' || !pageId) {
    return (
      <div className="flex flex-col  w-full">
        <Empty.NotFound title="页面不存在" description="当前页面处于回收站或被彻底删除" />
        <Footer />
      </div>
    );
  }

  if (!shared) {
    return (
      <div className="flex flex-col w-full">
        <Empty.NotShared />
        <Footer />
      </div>
    );
  }

  return (
    // 要保证main-panel的css跟非分享一致，否则右侧打开有bug
    <div className="main-panel w-full flex flex-col flex-1 h-full min-h-0 min-w-[200px]">
      <Header />
      <div className={cx('flex-1 min-h-0 flex')}>
        <ImagesProvider uuid={pageId}>
          <ShareMain />
          <UseTemplateButton />
        </ImagesProvider>
        <Drawer />
      </div>
      {!__BUILD_IN__ && <KnowledgeAIButton />}
    </div>
  );
};

export const ShareMain: FC<{ disable?: boolean }> = (props) => {
  useGlobalModalQueueEffect({ isShare: true, disable: props.disable });
  const pageId = useGetPageId();
  const block = usePickBlock(pageId, ['createdBy']);

  useEffect(() => {
    void (async () => {
      if (!block?.createdBy) return;

      const user = await getUserInfoByApi(block.createdBy);
      if (user?.inviteCode && !getSessionOp.inviteCode.get()) {
        refreshSessionParams(user?.inviteCode, block.uuid);
      }
      tryGetPromotionChannel(user?.promotionChannel);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [block?.createdBy]);

  return <Page uuid={pageId} />;
};
